import React, { useState, useRef, useEffect } from 'react';
import { Badge, Form } from 'react-bootstrap';
import { FaPen } from 'react-icons/fa';

const AVAILABLE_TAGS = [
  { value: 'accessibility', label: 'Accessibility', color: 'info' },
  { value: '3D printing', label: '3D Printing', color: 'danger' },
  { value: 'laser cutting', label: 'Laser Cutting', color: 'warning' },
  { value: 'embroidery', label: 'Embroidery', color: 'success' },
  { value: 'knitting', label: 'Knitting', color: 'secondary' },
];

/**
 * Inline-editable description and tag toggles for an agent.
 *
 * Props:
 *   agentName   - string
 *   description - string
 *   tags        - string[]
 *   onUpdate    - (agentName, { description?, tags? }) => Promise<void>
 *   compact     - bool  (if true, hides tags — for tight spaces like the graph palette)
 */
const EditableAgentMeta = ({ agentName, description, tags = [], onUpdate, compact = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description || '');
  const textareaRef = useRef(null);

  useEffect(() => { setDraft(description || ''); }, [description]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const saveDescription = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (description || '').trim()) {
      onUpdate(agentName, { description: trimmed });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveDescription(); }
    if (e.key === 'Escape') { setDraft(description || ''); setEditing(false); }
  };

  const toggleTag = (tagValue) => {
    const current = tags || [];
    const next = current.includes(tagValue)
      ? current.filter(t => t !== tagValue)
      : [...current, tagValue];
    onUpdate(agentName, { tags: next });
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      {/* Description */}
      {editing ? (
        <Form.Control
          ref={textareaRef}
          as="textarea"
          rows={2}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={saveDescription}
          onKeyDown={handleKeyDown}
          style={{ fontSize: '0.78rem', resize: 'vertical', marginBottom: '4px' }}
        />
      ) : (
        <div
          onClick={() => setEditing(true)}
          style={{
            fontSize: '0.78rem', color: '#6c757d', cursor: 'pointer',
            padding: '2px 4px', borderRadius: '4px', marginBottom: '4px',
            display: 'flex', alignItems: 'flex-start', gap: '4px',
          }}
          className="editable-desc-hover"
          title="Click to edit description"
        >
          <span style={{ flex: 1 }}>{description || 'No description — click to add'}</span>
          <FaPen style={{ fontSize: '0.55rem', marginTop: '3px', opacity: 0.4, flexShrink: 0 }} />
        </div>
      )}

      {/* Tags */}
      {!compact && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
          {AVAILABLE_TAGS.map(tag => {
            const active = (tags || []).includes(tag.value);
            return (
              <Badge
                key={tag.value}
                bg={active ? tag.color : 'light'}
                text={active ? 'white' : 'dark'}
                style={{
                  cursor: 'pointer', fontSize: '0.6rem',
                  border: active ? 'none' : '1px solid #dee2e6',
                  opacity: active ? 1 : 0.6,
                  transition: 'all 0.15s',
                }}
                onClick={() => toggleTag(tag.value)}
                title={active ? `Remove "${tag.label}" tag` : `Add "${tag.label}" tag`}
              >
                {tag.label}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export { AVAILABLE_TAGS };
export default EditableAgentMeta;
